<template>
  <div class="custom-time-input">
    <label v-if="label" class="input-label">{{ label }}</label>

    <div
      class="custom-time-input__control"
      :class="{ disabled, invalidated: error }"
    >
      <i class="far fa-clock custom-time-input__icon" />

      <input
        class="custom-time-input__field"
        type="text"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        maxlength="5"
        @input="onInput"
        @blur="onBlur"
        @focus="$emit('focus', $event)"
      />

      <span class="custom-time-input__divider" />

      <select
        class="custom-time-input__period"
        :value="periodValue"
        :disabled="disabled"
        @change="$emit('update:periodValue', $event.target.value)"
      >
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
    <Error v-if="error" variant="no-border" :message="customErrorMsg" />
  </div>
</template>

<script>
import Error from '@/components/common/Error';

export default {
  name: 'CustomTimeInput',
  components: {
    Error,
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    periodValue: {
      type: String,
      default: 'AM',
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '00:00',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    // Reloj de 12 horas: la hora va de 01 a 12 (00 equivale a 12).
    // Sin él, la hora va de 00 a 11.
    twelveHourClock: {
      type: Boolean,
      default: false,
    },
    error: {
      type: [String, Boolean],
      default: '',
    },
    customErrorMsg: {
      type: String,
      default() {
        return this.error;
      },
    },
  },
  methods: {
    normalizeTime(value, shouldComplete = false) {
      const digits = `${value || ''}`.replace(/\D/g, '').slice(0, 4);

      if (!digits) {
        return '';
      }

      let hour = digits.slice(0, 2);
      let minutes = digits.slice(2, 4);

      if (hour.length === 2) {
        const hourNumber = Number(hour);
        const normalizedHour = this.twelveHourClock
          ? hourNumber === 0
            ? 12
            : Math.min(hourNumber, 12)
          : Math.min(hourNumber, 11);

        hour = normalizedHour.toString().padStart(2, '0');
      }

      if (minutes.length === 2) {
        minutes = Math.min(Number(minutes), 59)
          .toString()
          .padStart(2, '0');
      }

      if (shouldComplete && hour.length === 1) {
        hour =
          this.twelveHourClock && hour === '0' ? '12' : hour.padStart(2, '0');
      }

      if (shouldComplete && hour.length === 2 && minutes.length === 0) {
        minutes = '00';
      }

      if (shouldComplete && minutes.length === 1) {
        minutes = minutes.padStart(2, '0');
      }

      return minutes.length > 0 || shouldComplete ? `${hour}:${minutes}` : hour;
    },
    onInput(event) {
      const value = this.normalizeTime(event.target.value);

      event.target.value = value;
      this.$emit('input', value);
    },
    onBlur(event) {
      const value = this.normalizeTime(event.target.value, true);

      event.target.value = value;
      this.$emit('input', value);
      this.$emit('blur', event);
    },
  },
};
</script>

<style scoped lang="scss">
.custom-time-input {
  width: 100%;
}

.input-label {
  color: #727272;
  font-size: 14px;
  font-weight: 400 !important;
  margin-bottom: 6px;
}

.custom-time-input__control {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px 78px;
  align-items: center;
  width: 100%;
  min-height: 38px;
  border: 1px solid #c5cdcf;
  border-radius: 4px;
  background: #fff;
  overflow: hidden;
}

.custom-time-input__control.disabled {
  background: #e9ecef;
}

.custom-time-input__control.invalidated {
  border-color: #ff0018;
  background-color: #f7d7da;
}

.custom-time-input__icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #ff7058;
  font-size: 16px;
  pointer-events: none;
}

.custom-time-input__field {
  width: 100%;
  height: 38px;
  border: 0;
  outline: none;
  padding: 7px 12px 7px 34px;
  color: #102d2b;
  font-size: 14px;
  background: transparent;
}

.custom-time-input__field::placeholder {
  color: #8b979a;
}

.custom-time-input__divider {
  width: 1px;
  height: 22px;
  background: #e4e8e9;
}

.custom-time-input__period {
  width: 100%;
  height: 38px;
  border: 0;
  outline: none;
  padding: 0 24px 0 12px;
  color: #102d2b;
  font-size: 14px;
  background-color: transparent;
  cursor: pointer;
}

.custom-time-input__period:disabled,
.custom-time-input__field:disabled {
  cursor: not-allowed;
}
</style>
